import React, { JSX } from 'react';
import { Box, Container, Grid, Paper, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { HowItWorksStep, stepLabels, walkthroughSteps } from './howItWorksData';
import { ImageContainer, StepNumber, StepSection } from './HowItWorksLayout';

export function ProgressOverview(): JSX.Element {
  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Stepper alternativeLabel sx={{ mb: 4 }}>
          {stepLabels.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Container>
  );
}

export function WalkthroughSections(): JSX.Element {
  return (
    <>
      {walkthroughSteps.map(step => (
        <StepSection key={step.stepNumber}>
          <Container>
            <StepContent {...step} />
          </Container>
        </StepSection>
      ))}
    </>
  );
}

function StepContent(props: HowItWorksStep): JSX.Element {
  const textContent = (
    <Grid size={{ xs: 12, md: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <StepNumber>{props.stepNumber}</StepNumber>
        <Box sx={{ ml: { xs: 0, md: 3 }, textAlign: { xs: 'center', md: 'left' } }}>
          <Typography variant="h3" component="h2" sx={{ color: 'primary.main', mb: 1 }}>
            {props.title}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
          >
            {props.icon}
            <Typography variant="body2" color="text.secondary">
              {props.subtitle}
            </Typography>
          </Box>
        </Box>
      </Box>
      {props.description.map(paragraph => (
        <Typography key={paragraph} variant="body1" paragraph>
          {paragraph}
        </Typography>
      ))}
      {props.note && <Callout prefix="Note:" text={props.note} />}
      {props.success && <SuccessCallout text={props.success} />}
    </Grid>
  );
  const imageContent = (
    <Grid size={{ xs: 12, md: 6 }}>
      <ImageContainer>
        <img src={props.imageSrc} alt={props.imageAlt} />
      </ImageContainer>
    </Grid>
  );

  return (
    <Grid container spacing={4} alignItems="center">
      {props.reversed ? (
        <>
          {imageContent}
          {textContent}
        </>
      ) : (
        <>
          {textContent}
          {imageContent}
        </>
      )}
    </Grid>
  );
}

function Callout(props: { prefix: string; text: string }): JSX.Element {
  return (
    <Paper
      sx={{
        p: 2,
        backgroundColor: 'action.hover',
        border: '1px solid',
        borderColor: 'action.active',
        mt: 2,
      }}
    >
      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
        <strong>{props.prefix}</strong> {props.text}
      </Typography>
    </Paper>
  );
}

function SuccessCallout(props: { text: string }): JSX.Element {
  return (
    <Paper sx={{ p: 2, backgroundColor: 'secondary.main', color: 'secondary.contrastText', mt: 2 }}>
      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
        🎉 {props.text}
      </Typography>
    </Paper>
  );
}
